import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import { useCart } from "../context/CartContext";
import { getPaymentStatus } from "../lib/api";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 20; // 40s max

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [orderId, setOrderId] = useState(null);
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (res.order_id) setOrderId(res.order_id);
        if (res.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          return;
        }
        if (res.payment_status === "failed" || res.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // transient network/500 — will retry
        console.debug("[PaymentSuccess] poll transient error:", e?.message || e);
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, POLL_MS);
    };

    poll();
    return () => { cancelled = true; };
    // MAX_ATTEMPTS, POLL_MS are module-scope constants, and refs never change identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, clear]);

  return (
    <div className="bg-[#050505] min-h-screen">
      <Navbar />
      <div className="pt-40 pb-24 max-w-3xl mx-auto px-6 md:px-12 text-center">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto text-[color:var(--gold)] animate-spin mb-8" />
            <p className="label-caps text-[color:var(--gold)] mb-4">Vérification du paiement</p>
            <h1 data-testid="payment-verifying" className="font-serif text-4xl md:text-5xl text-white mb-6 leading-tight">
              Un <em className="italic text-[color:var(--gold-soft)]">instant</em>…
            </h1>
            <p className="text-white/60 font-light">
              Nous confirmons votre transaction auprès de Stripe. Merci de ne pas fermer cette fenêtre.
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full border border-[color:var(--gold)] flex items-center justify-center mb-8">
              <Check size={24} className="text-[color:var(--gold)]" />
            </div>
            <p className="label-caps text-[color:var(--gold)] mb-4">Paiement confirmé</p>
            <h1 data-testid="payment-success" className="font-serif text-5xl md:text-6xl text-white mb-6 leading-tight">
              Merci de votre <em className="italic text-[color:var(--gold-soft)]">confiance</em>.
            </h1>
            <p className="text-white/60 font-light max-w-xl mx-auto mb-6">
              Votre commande {orderId && (
                <span className="text-white">#{orderId.slice(0, 8).toUpperCase()}</span>
              )} a été enregistrée. Un email de confirmation vous parviendra sous peu.
            </p>
            <p className="text-white/40 text-sm italic mb-12">
              Chaque flacon est préparé avec soin puis expédié sous 48 à 72h.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate("/")}
                data-testid="success-home-btn"
                className="btn-outline">Retour à l'accueil</button>
              <Link to="/contact" className="label-caps text-white/50 hover:text-[color:var(--gold)] text-[11px]" data-testid="success-contact-link">
                Nous contacter
              </Link>
            </div>
          </>
        )}

        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle size={48} className="mx-auto text-white/40 mb-8" />
            <p className="label-caps text-white/50 mb-4">Statut incertain</p>
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-6 leading-tight">
              Nous n'avons pas pu <em className="italic text-white/60">confirmer</em>.
            </h1>
            <p className="text-white/60 font-light max-w-xl mx-auto mb-8">
              {status === "timeout"
                ? "La confirmation prend plus de temps que prévu. Vérifiez votre boîte email — si le paiement a abouti, vous recevrez la confirmation dans quelques instants."
                : "Le paiement n'a pas été finalisé. Aucun montant n'a été prélevé."}
            </p>
            <button onClick={() => navigate("/checkout")} data-testid="retry-checkout-btn" className="btn-gold">
              Réessayer le paiement
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
